import { useCallback } from 'react';

import { analytics } from 'analytics';
import { useAppEnvironmentContext } from 'contexts/appEnvironmentContext';

import { sankeyObjectSchema } from './validation';

/*
 * This hook is used to manage the caching of diagrams in the local storage
 * or Office document settings. It provides methods to check if a diagram exists
 * in the cache, clear the cached diagram, save a diagram to the cache, and load
 * a diagram from the cache.
 */
export const useDiagramCache = (id: number) => {
  const { appEnvironment } = useAppEnvironmentContext();
  const cacheKey = `editor:diagram:${id}`;

  // For forward-compatibility with https://github.com/sankeyart/sankeyart/issues/2126
  const draftCacheKey = `editor:diagram:draft`;

  // Helper function to get the cached diagram value
  const get = useCallback((): string | null => {
    if (appEnvironment === 'office') {
      return Office.context.document.settings.get(cacheKey);
    } else {
      return localStorage.getItem(cacheKey);
    }
  }, [appEnvironment, cacheKey]);

  // Check if the diagram exists in the cache
  const exists = useCallback(() => !!get(), [get]);

  // Clear the cached diagram
  const clear = useCallback(() => {
    if (appEnvironment === 'office') {
      Office.context.document.settings.remove(cacheKey);
    } else {
      localStorage.removeItem(cacheKey);
    }
  }, [appEnvironment, cacheKey]);

  // Save the diagram to the cache
  const save = useCallback(
    (form: SankeyForm) => {
      const value = JSON.stringify({
        data: form.getValues(),
        timestamp: new Date().toISOString(),
      });
      if (appEnvironment === 'office') {
        Office.context.document.settings.set(draftCacheKey, value);
        Office.context.document.settings.set(cacheKey, value);
        Office.context.document.settings.saveAsync((asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            analytics.reportError(
              'Failed to save diagram to Office cache',
              { error: asyncResult.error, value },
              false
            );
          }
        });
      } else {
        localStorage.setItem(draftCacheKey, value);
        localStorage.setItem(cacheKey, value);
      }
    },
    [appEnvironment, cacheKey]
  );

  // Load the diagram from the cache
  const load = useCallback(
    (form: SankeyForm) => {
      // If the diagram is not cached, return false
      const value = get();
      if (!value) {
        return false;
      }
      // If the diagram is cached, parse it and validate it against the schema - if it
      // is not valid, clear the cache and return false
      const { data: sankey } = JSON.parse(value);
      if (!sankeyObjectSchema.isValidSync(sankey)) {
        console.warn('Removing invalid cached diagram:', sankey);
        clear();
        return false;
      }
      // If the diagram is valid, reset the form with the cached values and return true
      form.reset(sankey);
      return true;
    },
    [appEnvironment, cacheKey, clear]
  );

  return { exists, clear, save, load };
};
